import type { SignupCustomerForm } from '@/features/auth/types'

export type AdvicePrepExistsResult = {
  companyExists: boolean
  emailExists: boolean
}

/** Spec 5.1c request. The client, user, Cognito login and Stripe customer in one call. */
export type AdvicePrepClientPayload = SignupCustomerForm & { databaseName: string }

/**
 * The three token fields only ever arrive together, so they travel as one
 * object: a partial set is a contract violation rather than a state to handle.
 * Absent means 5.1c did not sign the user in and the wizard continues
 * unauthenticated — see D-13.
 */
export type AdvicePrepClientSession = {
  idToken: string
  refreshToken: string
  expiry: number
}

/** Spec 5.1c. `platformId` is deliberately absent — signup creates no platform. */
export type AdvicePrepClientResult =
  | { clientId: string; userId: string; stripeCustomerId: string; session?: AdvicePrepClientSession }
  | { error: string }

export function isAdvicePrepClientError(result: AdvicePrepClientResult): result is { error: string } {
  return 'error' in result
}

/** Spec 5.2. */
export type AdvicePrepSubscribeResult =
  | {
      isSuccessful: true
      subscriptionId: string
      monthlyAmount: number
      currency: string
      nextBillingDate: string | null
    }
  | { isSuccessful: false; error: string }

/** Spec 5.5. */
export type AdvicePrepCancelResult =
  | { isSuccessful: true; cancelsOn: string | null }
  | { isSuccessful: false; error: string }

/** Spec 5.7. */
export type AdvicePrepResumeResult =
  | { isSuccessful: true; nextBillingDate: string | null }
  | { isSuccessful: false; error: string }

/** Spec 5.9. `amountDue` is the whole invoice, including usage — not the flat fee. */
export type AdvicePrepOutstandingInvoice =
  | { outstanding: false }
  | {
      outstanding: true
      invoiceId: string
      amountDue: number
      currency: string
      dueDate: string | null
    }

/** Spec 5.9. */
export type AdvicePrepPayInvoiceResult =
  | { isSuccessful: true; status?: string; nextBillingDate?: string | null }
  | { isSuccessful: false; error: string }

export type AdvicePrepPaywallReason = 'signup_incomplete' | 'past_due' | 'subscription_ended'

export type AdvicePrepPaywall = {
  active: boolean
  reason: AdvicePrepPaywallReason
  since: string | null
}
